/**
 * A catalogue of commonly used vanilla Bedrock items, used to populate the
 * recipe item picker.
 *
 * This is deliberately a curated list rather than an exhaustive dump: it covers
 * the items people actually put in crafting recipes, and the picker still
 * accepts any hand-typed `namespace:name` id, so an item that is missing here
 * is never a dead end.
 *
 * Entries are stored as bare name-parts and composed with the `minecraft:`
 * prefix at read time, so the ids in this file stay short and hard to typo.
 */

export interface VanillaItem {
  /** Full id, e.g. "minecraft:stick". */
  id: string;
  /** Human label, e.g. "Stick". */
  name: string;
  /** Label of the group the item belongs to. */
  group: string;
}

type Entry = [namePart: string, label: string];

const GROUPS: { label: string; entries: Entry[] }[] = [
  {
    label: "Wood",
    entries: [
      ["oak_log", "Oak Log"],
      ["spruce_log", "Spruce Log"],
      ["birch_log", "Birch Log"],
      ["jungle_log", "Jungle Log"],
      ["acacia_log", "Acacia Log"],
      ["dark_oak_log", "Dark Oak Log"],
      ["mangrove_log", "Mangrove Log"],
      ["cherry_log", "Cherry Log"],
      ["crimson_stem", "Crimson Stem"],
      ["warped_stem", "Warped Stem"],
      ["oak_planks", "Oak Planks"],
      ["spruce_planks", "Spruce Planks"],
      ["birch_planks", "Birch Planks"],
      ["jungle_planks", "Jungle Planks"],
      ["acacia_planks", "Acacia Planks"],
      ["dark_oak_planks", "Dark Oak Planks"],
      ["mangrove_planks", "Mangrove Planks"],
      ["cherry_planks", "Cherry Planks"],
      ["bamboo_planks", "Bamboo Planks"],
      ["crimson_planks", "Crimson Planks"],
      ["warped_planks", "Warped Planks"],
      ["stick", "Stick"],
      ["bamboo", "Bamboo"],
      ["oak_slab", "Oak Slab"],
      ["oak_stairs", "Oak Stairs"],
      ["oak_fence", "Oak Fence"],
      ["oak_door", "Oak Door"],
      ["oak_trapdoor", "Oak Trapdoor"],
      ["oak_sign", "Oak Sign"],
      ["ladder", "Ladder"],
      ["bookshelf", "Bookshelf"],
      ["chest", "Chest"],
      ["barrel", "Barrel"],
      ["crafting_table", "Crafting Table"],
    ],
  },
  {
    label: "Stone & building",
    entries: [
      ["stone", "Stone"],
      ["cobblestone", "Cobblestone"],
      ["mossy_cobblestone", "Mossy Cobblestone"],
      ["smooth_stone", "Smooth Stone"],
      ["stone_bricks", "Stone Bricks"],
      ["deepslate", "Deepslate"],
      ["cobbled_deepslate", "Cobbled Deepslate"],
      ["granite", "Granite"],
      ["diorite", "Diorite"],
      ["andesite", "Andesite"],
      ["tuff", "Tuff"],
      ["calcite", "Calcite"],
      ["dripstone_block", "Dripstone Block"],
      ["blackstone", "Blackstone"],
      ["basalt", "Basalt"],
      ["obsidian", "Obsidian"],
      ["netherrack", "Netherrack"],
      ["end_stone", "End Stone"],
      ["purpur_block", "Purpur Block"],
      ["quartz_block", "Block of Quartz"],
      ["prismarine", "Prismarine"],
      ["sea_lantern", "Sea Lantern"],
      ["glowstone", "Glowstone"],
      ["sand", "Sand"],
      ["red_sand", "Red Sand"],
      ["sandstone", "Sandstone"],
      ["gravel", "Gravel"],
      ["clay", "Clay"],
      ["clay_ball", "Clay Ball"],
      ["brick", "Brick"],
      ["bricks", "Bricks"],
      ["terracotta", "Terracotta"],
      ["glass", "Glass"],
      ["glass_pane", "Glass Pane"],
      ["dirt", "Dirt"],
      ["coarse_dirt", "Coarse Dirt"],
      ["grass_block", "Grass Block"],
      ["ice", "Ice"],
      ["packed_ice", "Packed Ice"],
      ["snow_block", "Snow Block"],
      ["snowball", "Snowball"],
      ["iron_bars", "Iron Bars"],
    ],
  },
  {
    label: "Ores & minerals",
    entries: [
      ["coal", "Coal"],
      ["charcoal", "Charcoal"],
      ["raw_iron", "Raw Iron"],
      ["iron_ingot", "Iron Ingot"],
      ["iron_nugget", "Iron Nugget"],
      ["raw_gold", "Raw Gold"],
      ["gold_ingot", "Gold Ingot"],
      ["gold_nugget", "Gold Nugget"],
      ["raw_copper", "Raw Copper"],
      ["copper_ingot", "Copper Ingot"],
      ["diamond", "Diamond"],
      ["emerald", "Emerald"],
      ["lapis_lazuli", "Lapis Lazuli"],
      ["redstone", "Redstone Dust"],
      ["quartz", "Nether Quartz"],
      ["amethyst_shard", "Amethyst Shard"],
      ["netherite_scrap", "Netherite Scrap"],
      ["netherite_ingot", "Netherite Ingot"],
      ["flint", "Flint"],
      ["coal_block", "Block of Coal"],
      ["iron_block", "Block of Iron"],
      ["gold_block", "Block of Gold"],
      ["copper_block", "Block of Copper"],
      ["diamond_block", "Block of Diamond"],
      ["emerald_block", "Block of Emerald"],
      ["lapis_block", "Block of Lapis Lazuli"],
      ["redstone_block", "Block of Redstone"],
      ["netherite_block", "Block of Netherite"],
      ["amethyst_block", "Block of Amethyst"],
    ],
  },
  {
    label: "Tools & weapons",
    entries: [
      ["wooden_pickaxe", "Wooden Pickaxe"],
      ["stone_pickaxe", "Stone Pickaxe"],
      ["iron_pickaxe", "Iron Pickaxe"],
      ["golden_pickaxe", "Golden Pickaxe"],
      ["diamond_pickaxe", "Diamond Pickaxe"],
      ["netherite_pickaxe", "Netherite Pickaxe"],
      ["wooden_axe", "Wooden Axe"],
      ["stone_axe", "Stone Axe"],
      ["iron_axe", "Iron Axe"],
      ["diamond_axe", "Diamond Axe"],
      ["netherite_axe", "Netherite Axe"],
      ["wooden_shovel", "Wooden Shovel"],
      ["iron_shovel", "Iron Shovel"],
      ["diamond_shovel", "Diamond Shovel"],
      ["wooden_hoe", "Wooden Hoe"],
      ["iron_hoe", "Iron Hoe"],
      ["diamond_hoe", "Diamond Hoe"],
      ["wooden_sword", "Wooden Sword"],
      ["stone_sword", "Stone Sword"],
      ["iron_sword", "Iron Sword"],
      ["golden_sword", "Golden Sword"],
      ["diamond_sword", "Diamond Sword"],
      ["netherite_sword", "Netherite Sword"],
      ["bow", "Bow"],
      ["crossbow", "Crossbow"],
      ["arrow", "Arrow"],
      ["trident", "Trident"],
      ["shield", "Shield"],
      ["fishing_rod", "Fishing Rod"],
      ["flint_and_steel", "Flint and Steel"],
      ["shears", "Shears"],
      ["bucket", "Bucket"],
      ["water_bucket", "Water Bucket"],
      ["lava_bucket", "Lava Bucket"],
      ["milk_bucket", "Milk Bucket"],
      ["brush", "Brush"],
      ["spyglass", "Spyglass"],
    ],
  },
  {
    label: "Armour",
    entries: [
      ["leather_helmet", "Leather Cap"],
      ["leather_chestplate", "Leather Tunic"],
      ["leather_leggings", "Leather Pants"],
      ["leather_boots", "Leather Boots"],
      ["chainmail_helmet", "Chainmail Helmet"],
      ["chainmail_chestplate", "Chainmail Chestplate"],
      ["iron_helmet", "Iron Helmet"],
      ["iron_chestplate", "Iron Chestplate"],
      ["iron_leggings", "Iron Leggings"],
      ["iron_boots", "Iron Boots"],
      ["golden_helmet", "Golden Helmet"],
      ["golden_chestplate", "Golden Chestplate"],
      ["diamond_helmet", "Diamond Helmet"],
      ["diamond_chestplate", "Diamond Chestplate"],
      ["diamond_leggings", "Diamond Leggings"],
      ["diamond_boots", "Diamond Boots"],
      ["netherite_helmet", "Netherite Helmet"],
      ["netherite_chestplate", "Netherite Chestplate"],
      ["netherite_leggings", "Netherite Leggings"],
      ["netherite_boots", "Netherite Boots"],
      ["turtle_helmet", "Turtle Shell"],
      ["elytra", "Elytra"],
    ],
  },
  {
    label: "Food & farming",
    entries: [
      ["apple", "Apple"],
      ["golden_apple", "Golden Apple"],
      ["enchanted_golden_apple", "Enchanted Golden Apple"],
      ["bread", "Bread"],
      ["wheat", "Wheat"],
      ["wheat_seeds", "Wheat Seeds"],
      ["pumpkin_seeds", "Pumpkin Seeds"],
      ["melon_seeds", "Melon Seeds"],
      ["beetroot_seeds", "Beetroot Seeds"],
      ["carrot", "Carrot"],
      ["golden_carrot", "Golden Carrot"],
      ["potato", "Potato"],
      ["baked_potato", "Baked Potato"],
      ["beetroot", "Beetroot"],
      ["melon_slice", "Melon Slice"],
      ["melon_block", "Melon"],
      ["pumpkin", "Pumpkin"],
      ["carved_pumpkin", "Carved Pumpkin"],
      ["pumpkin_pie", "Pumpkin Pie"],
      ["cake", "Cake"],
      ["cookie", "Cookie"],
      ["sugar", "Sugar"],
      ["sugar_cane", "Sugar Cane"],
      ["egg", "Egg"],
      ["milk_bucket", "Milk Bucket"],
      ["honey_bottle", "Honey Bottle"],
      ["honeycomb", "Honeycomb"],
      ["beef", "Raw Beef"],
      ["cooked_beef", "Steak"],
      ["porkchop", "Raw Porkchop"],
      ["cooked_porkchop", "Cooked Porkchop"],
      ["chicken", "Raw Chicken"],
      ["cooked_chicken", "Cooked Chicken"],
      ["mutton", "Raw Mutton"],
      ["cooked_mutton", "Cooked Mutton"],
      ["rabbit", "Raw Rabbit"],
      ["cooked_rabbit", "Cooked Rabbit"],
      ["cod", "Raw Cod"],
      ["cooked_cod", "Cooked Cod"],
      ["salmon", "Raw Salmon"],
      ["cooked_salmon", "Cooked Salmon"],
      ["tropical_fish", "Tropical Fish"],
      ["pufferfish", "Pufferfish"],
      ["dried_kelp", "Dried Kelp"],
      ["mushroom_stew", "Mushroom Stew"],
      ["rabbit_stew", "Rabbit Stew"],
      ["beetroot_soup", "Beetroot Soup"],
      ["suspicious_stew", "Suspicious Stew"],
      ["sweet_berries", "Sweet Berries"],
      ["glow_berries", "Glow Berries"],
      ["chorus_fruit", "Chorus Fruit"],
      ["bone_meal", "Bone Meal"],
    ],
  },
  {
    label: "Nature",
    entries: [
      ["oak_sapling", "Oak Sapling"],
      ["spruce_sapling", "Spruce Sapling"],
      ["birch_sapling", "Birch Sapling"],
      ["jungle_sapling", "Jungle Sapling"],
      ["oak_leaves", "Oak Leaves"],
      ["dandelion", "Dandelion"],
      ["poppy", "Poppy"],
      ["blue_orchid", "Blue Orchid"],
      ["cornflower", "Cornflower"],
      ["sunflower", "Sunflower"],
      ["red_mushroom", "Red Mushroom"],
      ["brown_mushroom", "Brown Mushroom"],
      ["crimson_fungus", "Crimson Fungus"],
      ["warped_fungus", "Warped Fungus"],
      ["cactus", "Cactus"],
      ["vine", "Vines"],
      ["kelp", "Kelp"],
      ["seagrass", "Seagrass"],
      ["lily_pad", "Lily Pad"],
      ["moss_block", "Moss Block"],
      ["cocoa_beans", "Cocoa Beans"],
      ["nether_wart", "Nether Wart"],
      ["glow_lichen", "Glow Lichen"],
      ["torchflower", "Torchflower"],
      ["pitcher_plant", "Pitcher Plant"],
    ],
  },
  {
    label: "Redstone & transport",
    entries: [
      ["redstone_torch", "Redstone Torch"],
      ["repeater", "Redstone Repeater"],
      ["comparator", "Redstone Comparator"],
      ["piston", "Piston"],
      ["sticky_piston", "Sticky Piston"],
      ["observer", "Observer"],
      ["hopper", "Hopper"],
      ["dropper", "Dropper"],
      ["dispenser", "Dispenser"],
      ["lever", "Lever"],
      ["tripwire_hook", "Tripwire Hook"],
      ["target", "Target"],
      ["daylight_detector", "Daylight Detector"],
      ["noteblock", "Note Block"],
      ["redstone_lamp", "Redstone Lamp"],
      ["tnt", "TNT"],
      ["slime_ball", "Slimeball"],
      ["slime", "Slime Block"],
      ["honey_block", "Honey Block"],
      ["lightning_rod", "Lightning Rod"],
      ["rail", "Rail"],
      ["golden_rail", "Powered Rail"],
      ["detector_rail", "Detector Rail"],
      ["activator_rail", "Activator Rail"],
      ["minecart", "Minecart"],
      ["hopper_minecart", "Minecart with Hopper"],
      ["chest_minecart", "Minecart with Chest"],
      ["oak_boat", "Oak Boat"],
      ["saddle", "Saddle"],
      ["lead", "Lead"],
      ["carrot_on_a_stick", "Carrot on a Stick"],
    ],
  },
  {
    label: "Mob drops & brewing",
    entries: [
      ["bone", "Bone"],
      ["string", "String"],
      ["feather", "Feather"],
      ["leather", "Leather"],
      ["rabbit_hide", "Rabbit Hide"],
      ["rabbit_foot", "Rabbit's Foot"],
      ["rotten_flesh", "Rotten Flesh"],
      ["spider_eye", "Spider Eye"],
      ["fermented_spider_eye", "Fermented Spider Eye"],
      ["gunpowder", "Gunpowder"],
      ["ender_pearl", "Ender Pearl"],
      ["ender_eye", "Eye of Ender"],
      ["blaze_rod", "Blaze Rod"],
      ["blaze_powder", "Blaze Powder"],
      ["ghast_tear", "Ghast Tear"],
      ["magma_cream", "Magma Cream"],
      ["phantom_membrane", "Phantom Membrane"],
      ["shulker_shell", "Shulker Shell"],
      ["nautilus_shell", "Nautilus Shell"],
      ["heart_of_the_sea", "Heart of the Sea"],
      ["prismarine_shard", "Prismarine Shard"],
      ["prismarine_crystals", "Prismarine Crystals"],
      ["ink_sac", "Ink Sac"],
      ["glow_ink_sac", "Glow Ink Sac"],
      ["nether_star", "Nether Star"],
      ["dragon_breath", "Dragon's Breath"],
      ["totem_of_undying", "Totem of Undying"],
      ["experience_bottle", "Bottle o' Enchanting"],
      ["glass_bottle", "Glass Bottle"],
      ["potion", "Potion"],
      ["brewing_stand", "Brewing Stand"],
      ["cauldron", "Cauldron"],
      ["glistering_melon_slice", "Glistering Melon Slice"],
    ],
  },
  {
    label: "Utility & decoration",
    entries: [
      ["torch", "Torch"],
      ["soul_torch", "Soul Torch"],
      ["lantern", "Lantern"],
      ["soul_lantern", "Soul Lantern"],
      ["campfire", "Campfire"],
      ["furnace", "Furnace"],
      ["blast_furnace", "Blast Furnace"],
      ["smoker", "Smoker"],
      ["smithing_table", "Smithing Table"],
      ["grindstone", "Grindstone"],
      ["stonecutter_block", "Stonecutter"],
      ["loom", "Loom"],
      ["cartography_table", "Cartography Table"],
      ["fletching_table", "Fletching Table"],
      ["composter", "Composter"],
      ["anvil", "Anvil"],
      ["enchanting_table", "Enchanting Table"],
      ["ender_chest", "Ender Chest"],
      ["undyed_shulker_box", "Shulker Box"],
      ["beacon", "Beacon"],
      ["conduit", "Conduit"],
      ["respawn_anchor", "Respawn Anchor"],
      ["lodestone", "Lodestone"],
      ["bell", "Bell"],
      ["jukebox", "Jukebox"],
      ["scaffolding", "Scaffolding"],
      ["flower_pot", "Flower Pot"],
      ["item_frame", "Item Frame"],
      ["glow_item_frame", "Glow Item Frame"],
      ["painting", "Painting"],
      ["armor_stand", "Armor Stand"],
      ["white_bed", "White Bed"],
      ["white_wool", "White Wool"],
      ["white_carpet", "White Carpet"],
      ["book", "Book"],
      ["writable_book", "Book and Quill"],
      ["written_book", "Written Book"],
      ["paper", "Paper"],
      ["empty_map", "Empty Map"],
      ["compass", "Compass"],
      ["clock", "Clock"],
      ["name_tag", "Name Tag"],
      ["bundle", "Bundle"],
      ["firework_rocket", "Firework Rocket"],
      ["end_crystal", "End Crystal"],
    ],
  },
  {
    label: "Dyes",
    entries: [
      ["white_dye", "White Dye"],
      ["light_gray_dye", "Light Gray Dye"],
      ["gray_dye", "Gray Dye"],
      ["black_dye", "Black Dye"],
      ["brown_dye", "Brown Dye"],
      ["red_dye", "Red Dye"],
      ["orange_dye", "Orange Dye"],
      ["yellow_dye", "Yellow Dye"],
      ["lime_dye", "Lime Dye"],
      ["green_dye", "Green Dye"],
      ["cyan_dye", "Cyan Dye"],
      ["light_blue_dye", "Light Blue Dye"],
      ["blue_dye", "Blue Dye"],
      ["purple_dye", "Purple Dye"],
      ["magenta_dye", "Magenta Dye"],
      ["pink_dye", "Pink Dye"],
    ],
  },
];

/** Every catalogued item, flattened and de-duplicated by id, in group order. */
export const VANILLA_ITEMS: VanillaItem[] = (() => {
  const seen = new Set<string>();
  const out: VanillaItem[] = [];
  for (const group of GROUPS) {
    for (const [namePart, name] of group.entries) {
      const id = `minecraft:${namePart}`;
      if (seen.has(id)) continue;
      seen.add(id);
      out.push({ id, name, group: group.label });
    }
  }
  return out;
})();

const BY_ID = new Map(VANILLA_ITEMS.map((item) => [item.id, item]));

/** Looks up a catalogued item by full id. Bare name-parts are assumed vanilla. */
export function findVanillaItem(id: string): VanillaItem | undefined {
  const full = id.includes(":") ? id : `minecraft:${id}`;
  return BY_ID.get(full.trim().toLowerCase());
}
